// Settings — one hub for everything account-shaped, role-gated by section.
//
// Reached from the sidebar footer. Every role gets it; sections are gated:
//   * Account, Security, Walkthroughs — everyone (Walkthroughs behind the guided_tutorial flag)
//     Security lists where the person is signed in (SecuritySettings).
//   * Billing, Activity — org owners only
//
// Account (AgentProfile) merges the old My Profile and Account Settings tabs,
// which were two pages saying one thing. Subscription info lives only in
// Billing. The section components are the originals, composed unchanged.
//
// Walkthroughs lists every guided walkthrough (WALKTHROUGHS in tutorial/content.ts)
// with a Start button each, so any one of them can be replayed on its own — the
// sample client through the wizard, My Clients, the comparison, Downline
// Management — whatever the person answered on the welcome dialog, all on
// made-up data. Restart All plays the whole tour again.

import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GraduationCap, Play, RotateCcw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { hidesPersonalClients } from '@/lib/clientBook';
import { useTutorial } from '@/tutorial/TutorialContext';
import { WALKTHROUGHS, walkthroughVisible } from '@/tutorial/content';
import AgentProfile from '@/components/AgentProfile';
import SecuritySettings from '@/components/SecuritySettings';
import OrgBilling from '@/components/org/OrgBilling';
import OrgAuditLog from '@/components/org/OrgAuditLog';

export default function SettingsPage() {
  const { profile, isSuperAdmin, isOrgOwner, isOrgAdmin, isPlatformAdmin, org } = useAuth();
  const { enabled: tutorialEnabled, startWalkthrough, startFullTour } = useTutorial();

  const showOwnerSections = isOrgOwner && !!org;
  // Downline Management is the org owner's, org admin's and platform admin's
  // page (its route allows exactly those); nobody else is offered its walkthrough.
  // My Clients goes to anyone with a client book, All Clients to a support
  // admin without one (the same rule as the sidebar).
  const canSeeDownline = isOrgOwner || isOrgAdmin || isPlatformAdmin;
  const noClientBook = hidesPersonalClients({ hasClientBook: profile?.has_client_book, isSuperAdmin, isOrgOwner, isOrgAdmin });
  const walkthroughs = WALKTHROUGHS.filter(w => walkthroughVisible(w.requires, { canSeeDownline, noClientBook }));

  const sections = [
    { value: 'account', label: 'Account', show: true },
    { value: 'security', label: 'Security', show: true },
    { value: 'walkthroughs', label: 'Walkthroughs', show: tutorialEnabled },
    { value: 'billing', label: 'Billing', show: showOwnerSections },
    { value: 'activity', label: 'Activity', show: showOwnerSections },
  ].filter(s => s.show);

  const [tab, setTab] = useState(sections[0]?.value ?? 'account');

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-2.5 flex-wrap">
        <h2 className="text-xl font-semibold tracking-tight">Settings</h2>
        <p className="text-xs text-muted-foreground">
          Your profile, the walkthroughs, and account details — one page.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          {sections.map(s => (
            <TabsTrigger key={s.value} value={s.value} data-tour={`settings-${s.value}`}>
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="account">
          <AgentProfile />
        </TabsContent>

        <TabsContent value="security">
          <SecuritySettings />
        </TabsContent>

        {tutorialEnabled && (
          <TabsContent value="walkthroughs">
            <div className="space-y-4 max-w-2xl">
              <div className="flex items-start gap-2">
                <GraduationCap className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Guided tours of the application on sample data: nothing to enter, nothing
                  saved. Restart All plays every section in order, as a new user sees it. Each
                  section can also be started on its own; it opens the page it covers and walks
                  through it.
                </p>
              </div>
              <Card data-tour="walkthrough-all">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">The whole tour</CardTitle>
                  <CardDescription>
                    {walkthroughs.map(w => w.title).join(', then ')}. Each section leads into
                    the next.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button size="sm" onClick={startFullTour} aria-label="Restart All">
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                    Restart All
                  </Button>
                </CardContent>
              </Card>
              {walkthroughs.map(w => (
                <Card key={w.id} data-tour={`walkthrough-${w.id}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{w.title}</CardTitle>
                    <CardDescription>{w.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button size="sm" onClick={() => startWalkthrough(w.id)} aria-label={`Start ${w.title}`}>
                      <Play className="h-3.5 w-3.5 mr-1" />
                      Start
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}

        {showOwnerSections && (
          <>
            <TabsContent value="billing">
              <OrgBilling />
            </TabsContent>
            <TabsContent value="activity">
              <OrgAuditLog />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
